import { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { CalendarDays, Plus, Check, X, Clock, AlertTriangle, TrendingUp, CalendarCheck2, Info, Send, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { MONTHS_FR, DAYS_SHORT_3 as DAYS_SHORT, API_BASE as API } from '../utils/constants';

const STATUS_CONFIG = {
  pending:  { label: 'En attente', color: 'bg-[var(--warning)]', text: 'text-white', icon: Clock },
  approved: { label: 'Approuvé',   color: 'bg-[var(--success)]', text: 'text-white', icon: Check },
  rejected: { label: 'Refusé',     color: 'bg-[var(--error)]',   text: 'text-white', icon: X },
  cancelled:{ label: 'Annulé',     color: 'bg-[var(--neutral-400)]', text: 'text-white', icon: X },
};

// ── Single Month Calendar Component (style cohérent avec MonthView du pointage) ──
function LeaveCalendar({ month, year, leaveMap }) {
  const firstDay = new Date(year, month, 1);
  let startDow = firstDay.getDay();
  startDow = startDow === 0 ? 6 : startDow - 1; // Monday = 0
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const today = new Date();
  const todayStr = `${today.getFullYear()}-${String(today.getMonth()+1).padStart(2,'0')}-${String(today.getDate()).padStart(2,'0')}`;

  const cells = [];
  for (let i = 0; i < startDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push(`${year}-${String(month+1).padStart(2,'0')}-${String(d).padStart(2,'0')}`);
  }

  return (
    <div className="grid grid-cols-7 gap-1">
      {DAYS_SHORT.map((d) => (
        <div key={d} className="text-center text-xs font-semibold py-1" style={{ color: 'var(--text-secondary)' }}>{d}</div>
      ))}
      {cells.map((dateStr, i) => {
        if (!dateStr) return <div key={`e-${i}`} className="h-[52px]" />;
        const dayNum = parseInt(dateStr.split('-')[2], 10);
        const leave = leaveMap.get(dateStr);
        const isToday = dateStr === todayStr;
        const dow = new Date(dateStr + 'T12:00:00').getDay();
        const isWeekend = dow === 0 || dow === 6;
        const isSunday = dow === 0;

        let tooltip = null;
        const cell = (
          <div
            key={dateStr}
            className={cn(
              'relative rounded-lg border p-1.5 flex flex-col items-center justify-center h-[52px] transition-all duration-150',
              // Approved: green background (like "Déjà saisi" in pointage)
              leave?.status === 'approved' && 'bg-[var(--success)]/15 border-[var(--success)]',
              // Pending: orange/warning background (like "Absence" in pointage)
              leave?.status === 'pending' && 'bg-[var(--warning)]/15 border-[var(--warning)]',
              // Rejected: error style
              leave?.status === 'rejected' && 'bg-[var(--error)]/10 border-[var(--error)]/40',
              // Today
              isToday && !leave && 'bg-[var(--primary-50)] border-[var(--primary-400)] ring-1 ring-[var(--primary-400)]',
              // Weekend
              isWeekend && !isToday && !leave && 'bg-[var(--neutral-50)] border-[var(--neutral-200)]',
              // Normal day
              !isToday && !isWeekend && !leave && 'border-[var(--neutral-200)]',
            )}
            data-testid={`leave-cal-day-${dayNum}`}
          >
            <span className={cn(
              'text-xs font-semibold leading-none',
              leave?.status === 'approved' && 'text-[var(--success)]',
              leave?.status === 'pending' && 'text-[var(--warning)]',
              leave?.status === 'rejected' && 'text-[var(--error)]',
              isToday && !leave && 'text-[var(--primary-400)]',
              !leave && !isToday && 'text-[var(--text-secondary)]',
              isSunday && !leave && 'opacity-40',
            )}>
              {dayNum}
            </span>
            {leave && (
              <span className={cn(
                'text-[8px] font-medium leading-tight mt-0.5 text-center',
                leave.status === 'approved' && 'text-[var(--success)]',
                leave.status === 'pending' && 'text-[var(--warning)]',
                leave.status === 'rejected' && 'text-[var(--error)]',
              )}>
                {leave.status === 'approved' ? 'Congé' : leave.status === 'pending' ? 'Att.' : 'Ref.'}
              </span>
            )}
          </div>
        );

        if (leave) {
          tooltip = leave.status === 'approved' ? 'Congé approuvé' : leave.status === 'pending' ? 'En attente d\'approbation' : 'Congé refusé';
          return (
            <Tooltip key={dateStr}>
              <TooltipTrigger asChild>{cell}</TooltipTrigger>
              <TooltipContent side="top" className="text-xs py-1 px-2">{tooltip}</TooltipContent>
            </Tooltip>
          );
        }
        return cell;
      })}
    </div>
  );
}

export default function LeaveManagement() {
  const { user } = useAuth();
  const isParent = user?.role === 'parent';
  const isNanny = user?.role === 'nanny';

  const [contracts, setContracts] = useState([]);
  const [selectedContract, setSelectedContract] = useState('');
  const [selectedContractData, setSelectedContractData] = useState(null);
  const [balance, setBalance] = useState(null);
  const [leaves, setLeaves] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingBalance, setLoadingBalance] = useState(false);

  const [showRequestForm, setShowRequestForm] = useState(false);
  const [requestStartDate, setRequestStartDate] = useState('');
  const [requestEndDate, setRequestEndDate] = useState('');
  const [requestNotes, setRequestNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [indemnity, setIndemnity] = useState(null);

  // Mini calendar navigation
  const [calStartMonth, setCalStartMonth] = useState(() => new Date().getMonth());
  const [calStartYear, setCalStartYear] = useState(() => new Date().getFullYear());

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/contracts`);
        const cList = res.data || [];
        setContracts(cList);
        if (cList.length > 0) { setSelectedContract(cList[0].id); setSelectedContractData(cList[0]); }
      } catch (err) { console.warn('[LeaveManagement] contracts fetch:', err.message || err); setContracts([]); }
    })();
  }, []);

  const fetchData = useCallback(async () => {
    if (!selectedContract) return;
    setLoading(true);
    setLoadingBalance(true);
    try {
      const [balRes, leaveRes] = await Promise.all([
        axios.get(`${API}/leaves/balance/${selectedContract}`),
        axios.get(`${API}/leaves?contract_id=${selectedContract}`),
      ]);
      setBalance(balRes.data);
      setLeaves(leaveRes.data || []);
      const c = contracts.find(c => c.id === selectedContract);
      setSelectedContractData(c || null);
    } catch (err) { console.error(err); }
    finally { setLoading(false); setLoadingBalance(false); }
  }, [selectedContract, contracts]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const yearType = selectedContractData?.contract_year_type || balance?.year_type || 'complete';
  const isFullYear = yearType === 'complete';

  const handleSubmitRequest = async (e) => {
    e.preventDefault();
    if (!requestStartDate || !requestEndDate) { toast.error('Sélectionnez les dates'); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/leaves/request`, {
        contract_id: selectedContract,
        start_date: requestStartDate,
        end_date: requestEndDate,
        notes: requestNotes || null,
      });
      toast.success(res.data.message || 'Demande envoyée');
      setShowRequestForm(false);
      setRequestStartDate(''); setRequestEndDate(''); setRequestNotes(''); setIndemnity(null);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
    finally { setSubmitting(false); }
  };

  useEffect(() => {
    if (!requestStartDate || !requestEndDate || requestEndDate < requestStartDate) { setIndemnity(null); return; }
    const start = new Date(requestStartDate + 'T12:00:00');
    const end = new Date(requestEndDate + 'T12:00:00');
    let days = 0; const cur = new Date(start);
    while (cur <= end) { if (cur.getDay() !== 0) days++; cur.setDate(cur.getDate() + 1); }
    (async () => {
      try { const r = await axios.post(`${API}/leaves/calculate`, { contract_id: selectedContract, days }); setIndemnity(r.data); }
      catch (err) { console.warn('[LeaveManagement] indemnity calc:', err.message || err); setIndemnity(null); }
    })();
  }, [requestStartDate, requestEndDate, selectedContract]);

  const handleApprove = async (id) => {
    try { await axios.put(`${API}/leaves/${id}/approve`); toast.success('Congé approuvé'); fetchData(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };
  const handleReject = async (id) => {
    try { await axios.put(`${API}/leaves/${id}/reject`); toast.success('Congé refusé'); fetchData(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const daysPreview = useMemo(() => {
    if (!requestStartDate || !requestEndDate) return 0;
    const s = new Date(requestStartDate+'T12:00:00'), e = new Date(requestEndDate+'T12:00:00');
    let d=0; const c=new Date(s); while(c<=e){if(c.getDay()!==0)d++;c.setDate(c.getDate()+1);} return d;
  }, [requestStartDate, requestEndDate]);

  const pendingCount = leaves.filter(l => l.status === 'pending').length;

  // Build leave date map for calendar
  const leaveMap = useMemo(() => {
    const m = new Map();
    for (const l of leaves) {
      if (!l.start_date || !l.end_date) continue;
      const s = new Date(l.start_date + 'T12:00:00');
      const e = new Date(l.end_date + 'T12:00:00');
      const cur = new Date(s);
      while (cur <= e) {
        if (cur.getDay() !== 0) {
          const ds = `${cur.getFullYear()}-${String(cur.getMonth()+1).padStart(2,'0')}-${String(cur.getDate()).padStart(2,'0')}`;
          if (!m.has(ds) || l.status === 'approved') m.set(ds, l);
        }
        cur.setDate(cur.getDate() + 1);
      }
    }
    return m;
  }, [leaves]);

  // Single month for calendar (matches TimeTracking MonthView style)

  const shiftCal = (dir) => {
    let m = calStartMonth + dir;
    let y = calStartYear;
    if (m > 11) { m = 0; y += 1; }
    if (m < 0) { m = 11; y -= 1; }
    setCalStartMonth(m); setCalStartYear(y);
  };

  return (
    <div className="container-custom py-6 space-y-6" data-testid="leave-management-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold" style={{ fontFamily: 'var(--font-secondary)', color: 'var(--text-primary)' }}>
            Gestion des congés
          </h1>
          <p className="text-xs mt-0.5" style={{ color: 'var(--text-secondary)' }}>
            {isFullYear ? 'Année complète — congés inclus dans la mensualisation' : 'Année incomplète — indemnités calculées'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {contracts.length > 1 && (
            <Select value={selectedContract} onValueChange={setSelectedContract}>
              <SelectTrigger className="w-36 h-9 text-xs" data-testid="leave-contract-select"><SelectValue /></SelectTrigger>
              <SelectContent>{contracts.map(c => (
                <SelectItem key={c.id} value={c.id}>{c.child_first_name || c.child_name}</SelectItem>
              ))}</SelectContent>
            </Select>
          )}
          <Button size="sm" onClick={() => setShowRequestForm(true)} style={{ backgroundColor: 'var(--primary-400)', color: 'white' }} data-testid="new-leave-request-btn">
            <Plus className="h-4 w-4 mr-1" /> Demander
          </Button>
        </div>
      </div>

      {/* Balance cards */}
      {loadingBalance ? (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {Array.from({length:4}).map((_,i)=><Skeleton key={i} className="h-20 rounded-xl" />)}
        </div>
      ) : balance ? (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Card className="border-[var(--border-light)]" data-testid="leave-balance-acquired">
            <CardContent className="p-3">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: 'var(--primary-100)' }}>
                  <TrendingUp className="h-4 w-4" style={{ color: 'var(--primary-500)' }} />
                </div>
                <div>
                  <p className="text-xl font-bold" style={{ color: 'var(--text-primary)' }}>{balance.days_acquired}</p>
                  <p className="text-[10px]" style={{ color: 'var(--text-tertiary)' }}>Acquis</p>
                </div>
              </div>
              <p className="text-[9px] mt-1" style={{ color: 'var(--text-tertiary)' }}>{balance.months_worked} mois × 2,5j</p>
            </CardContent>
          </Card>
          <Card className="border-[var(--border-light)]" data-testid="leave-balance-taken">
            <CardContent className="p-3">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: 'var(--secondary-100)' }}>
                  <CalendarCheck2 className="h-4 w-4" style={{ color: 'var(--secondary-500)' }} />
                </div>
                <div>
                  <p className="text-xl font-bold" style={{ color: 'var(--text-primary)' }}>{balance.days_taken}</p>
                  <p className="text-[10px]" style={{ color: 'var(--text-tertiary)' }}>Pris</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className={cn("border-[var(--border-light)]", balance.days_remaining < 0 && "border-[var(--error)]/50")} data-testid="leave-balance-remaining">
            <CardContent className="p-3">
              <div className="flex items-center gap-2">
                <div className={cn("w-8 h-8 rounded-full flex items-center justify-center", balance.days_remaining < 0 ? "bg-[var(--error)]/10" : "bg-[var(--success)]/10")}>
                  <CalendarDays className="h-4 w-4" style={{ color: balance.days_remaining < 0 ? 'var(--error)' : 'var(--success)' }} />
                </div>
                <div>
                  <p className="text-xl font-bold" style={{ color: balance.days_remaining < 0 ? 'var(--error)' : 'var(--text-primary)' }}>{balance.days_remaining}</p>
                  <p className="text-[10px]" style={{ color: 'var(--text-tertiary)' }}>Restant</p>
                </div>
              </div>
              {balance.days_remaining < 0 && (
                <div className="flex items-center gap-1 mt-1">
                  <AlertTriangle className="h-3 w-3 text-[var(--error)]" />
                  <p className="text-[9px] text-[var(--error)]">Anticipé</p>
                </div>
              )}
            </CardContent>
          </Card>
          <Card className="border-[var(--border-light)]">
            <CardContent className="p-3">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full flex items-center justify-center bg-[var(--neutral-100)]">
                  <Info className="h-4 w-4" style={{ color: 'var(--text-tertiary)' }} />
                </div>
                <div>
                  <p className="text-xs font-bold" style={{ color: 'var(--text-primary)' }}>{isFullYear ? 'Complète' : 'Incomplète'}</p>
                  <p className="text-[10px]" style={{ color: 'var(--text-tertiary)' }}>Type d'année</p>
                </div>
              </div>
              <p className="text-[9px] mt-1" style={{ color: 'var(--text-tertiary)' }}>
                {new Date(balance.period_start+'T12:00:00').toLocaleDateString('fr-FR',{month:'short',year:'numeric'})} → {new Date(balance.period_end+'T12:00:00').toLocaleDateString('fr-FR',{month:'short',year:'numeric'})}
              </p>
            </CardContent>
          </Card>
        </div>
      ) : null}

      {/* Full year info */}
      {isFullYear && balance && (
        <div className="p-3 rounded-lg border border-[var(--primary-400)]/20 bg-[var(--primary-400)]/5 flex items-start gap-2" data-testid="full-year-info">
          <Info className="h-4 w-4 shrink-0 mt-0.5" style={{ color: 'var(--primary-400)' }} />
          <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>
            <strong>Congés inclus dans le salaire mensuel.</strong> Contrat année complète : le salaire est identique chaque mois.
          </p>
        </div>
      )}

      {/* Pending alert */}
      {isParent && pendingCount > 0 && (
        <div className="p-3 rounded-lg border border-[var(--warning)]/30 bg-[var(--warning)]/5 flex items-center gap-2" data-testid="pending-leaves-alert">
          <Clock className="h-4 w-4 text-[var(--warning)]" />
          <p className="text-xs font-medium" style={{ color: 'var(--text-primary)' }}>
            {pendingCount} demande{pendingCount > 1 ? 's' : ''} en attente
          </p>
        </div>
      )}

      {/* Calendar — full width, single month */}
      <Card className="border-[var(--border-light)]" data-testid="leave-mini-calendar">
        <CardHeader className="p-4 pb-2">
          <div className="flex items-center justify-between">
            <Button variant="outline" size="icon" className="h-8 w-8 shrink-0" onClick={() => shiftCal(-1)} data-testid="cal-prev">
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm font-semibold text-center capitalize" style={{ fontFamily: 'var(--font-secondary)', color: 'var(--text-primary)' }} data-testid="cal-month-label">
              {MONTHS_FR[calStartMonth]} {calStartYear}
            </span>
            <Button variant="outline" size="icon" className="h-8 w-8 shrink-0" onClick={() => shiftCal(1)} data-testid="cal-next">
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-4 pt-0">
          <TooltipProvider delayDuration={150}>
            <LeaveCalendar month={calStartMonth} year={calStartYear} leaveMap={leaveMap} />
          </TooltipProvider>

          {/* Legend — same style as MonthView */}
          <div className="flex items-center gap-4 mt-4 text-xs flex-wrap" style={{ color: 'var(--text-secondary)' }}>
            <div className="flex items-center gap-1.5">
              <div className="w-4 h-4 rounded bg-[var(--success)]/15 border border-[var(--success)]" /><span>Approuvé</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-4 h-4 rounded bg-[var(--warning)]/15 border border-[var(--warning)]" /><span>En attente</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-4 h-4 rounded border-2 border-[var(--primary-400)]" /><span>Aujourd'hui</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Leave Request Dialog */}
      <Dialog open={showRequestForm} onOpenChange={setShowRequestForm}>
        <DialogContent className="bg-[var(--bg-primary)] sm:max-w-md" style={{ backgroundColor: 'var(--bg-primary)' }} data-testid="leave-request-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'var(--font-secondary)' }}>Demander un congé</DialogTitle>
            <DialogDescription>Jours ouvrables (lundi → samedi)</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmitRequest} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Début *</Label>
                <Input type="date" value={requestStartDate} onChange={(e) => setRequestStartDate(e.target.value)} required data-testid="leave-start-date" />
              </div>
              <div>
                <Label className="text-xs">Fin *</Label>
                <Input type="date" min={requestStartDate} value={requestEndDate} onChange={(e) => setRequestEndDate(e.target.value)} required data-testid="leave-end-date" />
              </div>
            </div>
            {daysPreview > 0 && (
              <div className="p-3 rounded-lg border border-[var(--primary-400)]/20 bg-[var(--primary-400)]/5 space-y-1">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{daysPreview} jour{daysPreview>1?'s':''} ouvrable{daysPreview>1?'s':''}</span>
                  {balance && <span className="text-[10px]" style={{ color: balance.days_remaining - daysPreview < 0 ? 'var(--error)' : 'var(--text-secondary)' }}>Solde après : {balance.days_remaining - daysPreview}j</span>}
                </div>
                {indemnity && !isFullYear && indemnity.amount > 0 && (
                  <p className="text-xs" style={{ color: 'var(--secondary-500)' }}>
                    Indemnité : {indemnity.amount.toFixed(2)}€ (méthode {indemnity.method})
                  </p>
                )}
                {indemnity && isFullYear && (
                  <p className="text-[10px]" style={{ color: 'var(--primary-400)' }}>ℹ️ {indemnity.message}</p>
                )}
              </div>
            )}
            <div>
              <Label className="text-xs">Notes</Label>
              <Textarea value={requestNotes} onChange={(e) => setRequestNotes(e.target.value)} placeholder="Vacances, raison personnelle..." rows={2} data-testid="leave-notes-input" />
            </div>
            {balance?.auto_approve && <Badge variant="outline" className="text-[10px] border-[var(--success)] text-[var(--success)]">Acceptation automatique</Badge>}
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" size="sm" onClick={() => { setShowRequestForm(false); setIndemnity(null); }}>Annuler</Button>
              <Button type="submit" size="sm" disabled={submitting || daysPreview===0} style={{ backgroundColor: 'var(--primary-400)', color: 'white' }} data-testid="submit-leave-request-btn">
                {submitting ? 'Envoi...' : <><Send className="h-3.5 w-3.5 mr-1" /> Envoyer</>}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Leave Requests List */}
      <Card className="border-[var(--border-light)]" data-testid="leave-requests-list">
        <CardHeader className="p-4 pb-2">
          <CardTitle className="text-sm flex items-center gap-2" style={{ fontFamily: 'var(--font-secondary)', color: 'var(--text-primary)' }}>
            Demandes de congés
            {pendingCount > 0 && <Badge className="text-[10px] bg-[var(--warning)] text-white">{pendingCount}</Badge>}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4 pt-0">
          {loading ? (
            <div className="space-y-2">{Array.from({length:3}).map((_,i)=><Skeleton key={i} className="h-14 rounded-lg" />)}</div>
          ) : leaves.length === 0 ? (
            <div className="text-center py-8">
              <CalendarDays className="h-10 w-10 mx-auto mb-2" style={{ color: 'var(--neutral-300)' }} />
              <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>Aucune demande</p>
              <p className="text-xs mt-0.5" style={{ color: 'var(--text-tertiary)' }}>
                {isNanny ? 'Cliquez "Demander" pour commencer' : 'Pas encore de demande de la nounou'}
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {leaves.map(leave => {
                const cfg = STATUS_CONFIG[leave.status] || STATUS_CONFIG.pending;
                const StatusIcon = cfg.icon;
                const sD = new Date(leave.start_date+'T12:00:00');
                const eD = new Date(leave.end_date+'T12:00:00');
                const sL = sD.toLocaleDateString('fr-FR',{day:'numeric',month:'short'});
                const eL = eD.toLocaleDateString('fr-FR',{day:'numeric',month:'short',year:'numeric'});
                return (
                  <div key={leave.id} className={cn(
                    "flex items-center justify-between p-3 rounded-lg border transition-colors duration-150",
                    leave.status==='pending' ? "border-[var(--warning)]/30 bg-[var(--warning)]/5" : "border-[var(--border-light)]"
                  )} data-testid={`leave-request-${leave.id}`}>
                    <div className="flex items-center gap-2.5 min-w-0">
                      <div className={cn("w-7 h-7 rounded-full flex items-center justify-center shrink-0", cfg.color)}>
                        <StatusIcon className={cn("h-3.5 w-3.5", cfg.text)} />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                          {sL} → {eL}
                          {leave.child_name && <span className="text-[10px] ml-1.5" style={{color:'var(--text-tertiary)'}}>({leave.child_name})</span>}
                        </p>
                        <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                          {leave.days_ouvrables}j ouvrable{leave.days_ouvrables>1?'s':''}
                          {leave.notes && <> — {leave.notes}</>}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0 ml-2">
                      <Badge variant="outline" className={cn("text-[10px]",
                        leave.status==='pending' && 'border-[var(--warning)] text-[var(--warning)]',
                        leave.status==='approved' && 'border-[var(--success)] text-[var(--success)]',
                        leave.status==='rejected' && 'border-[var(--error)] text-[var(--error)]',
                      )}>{cfg.label}</Badge>
                      {isParent && leave.status==='pending' && (
                        <div className="flex gap-1">
                          <Button size="icon" className="h-7 w-7 bg-[var(--success)] text-white hover:bg-[var(--success)]/80" onClick={() => handleApprove(leave.id)} data-testid={`approve-leave-${leave.id}`}>
                            <Check className="h-3.5 w-3.5" />
                          </Button>
                          <Button size="icon" variant="outline" className="h-7 w-7 border-[var(--error)] text-[var(--error)] hover:bg-[var(--error)]/10" onClick={() => handleReject(leave.id)} data-testid={`reject-leave-${leave.id}`}>
                            <X className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
